import React, { useEffect } from 'react';

import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import CustomerAdminIcon from '@mui/icons-material/WorkspacePremium';

import { useAppDispatch } from 'src/store';
import {
    setEditUserFormMode,
    setEditUserFormState,
    updateGeneralUiState,
    updaterUserManagementListFilterState,
} from 'src/app/admin/views/user-management/state/userManagementSlice';

import useCustomer from 'src/app/customers/hooks/useCustomer';
import useAuthService from 'src/shared/hooks/useAuthService';
import useUserManagementList from 'src/app/admin/views/user-management/hooks/useUserManagementList';

import { TertiaryButton } from 'src/shared/components/button/Button';
import { Access, Role, UserType } from 'src/shared/types';
import { DataTableColumns } from 'src/shared/components/data-table/dataTableTypes';
import { DataTableState, TableRowData } from 'src/shared/components/data-table/dataTableTypes';
import DataTable from 'src/shared/components/data-table/DataTable';
import Loader from 'src/shared/components/loader/Loader';
import StatusChip from 'src/shared/components/status-chip/StatusChip';

import UserTypeTag from 'src/app/admin/views/user-management/components/user-type-tag/UserTypeTag';
import UserDetails from 'src/app/admin/views/user-management/components/user-details/UserDetails';
import {
    getUserStatusFromEnum,
    checkIfCustomerAdmin,
    getUserRoleCount,
    getProjectNameForProjectId,
} from 'src/app/admin/views/user-management/userManagementHelpers';

import styles from './SuperAdminUserManagementList.module.scss';

const tableColumns: DataTableColumns = [
    {
        columnId: 'userId',
        title: 'User Id',
        width: '0',
    },
    {
        columnId: 'customerAdminLogo',
        title: 'Cust. admin',
        width: '70px',
    },
    {
        columnId: 'customerName',
        title: 'Name',
        width: '400px',
    },
    {
        columnId: 'accountStatus',
        title: 'Account Status',
        width: '200px',
    },
    {
        columnId: 'userType',
        title: 'User type',
        width: '108px',
    },
    {
        columnId: 'roles',
        title: 'Roles',
        width: '86px',
    },
    {
        columnId: 'assignedProjects',
        title: 'Assigned Projects',
        width: '90%',
    },
    {
        columnId: 'actions',
        title: 'Actions',
    },
];

const SuperAdminUserManagementList = () => {
    const { currentCustomerId, currentCustomerProjects } = useCustomer();
    const { isCurrentUserOnboardingManager } = useAuthService();
    const dispatch = useAppDispatch();

    const { isFetchingUsersForSuperAdmin, currentDataForSuperAdmin, sortedUsersForSuperAdmin, tableState, setTableState } =
        useUserManagementList();

    const users = [...(currentDataForSuperAdmin?.users || [])].filter(Boolean);

    const onRowSelect = (rowData: TableRowData) => {
        const userId = rowData[0] as string;
        if (userId) {
            const editUserDetails = users.find(user => user.cognitoId === userId);
            if (!editUserDetails) {
                return;
            }
            const userRoles = editUserDetails.customers.find(customer => customer.customerId === currentCustomerId)?.roles;
            dispatch(
                setEditUserFormState({
                    ...editUserDetails,
                    projects: editUserDetails.projects || [],
                    roles: userRoles as Role[],
                })
            );

            dispatch(updateGeneralUiState({ shouldShowViewUserModal: true }));
        }
    };

    useEffect(() => {
        if (!currentDataForSuperAdmin || !currentCustomerProjects) {
            return;
        }

        const projectIdsForCurrentCustomer = currentCustomerProjects.map(project => project.projectId) || [];
        const tableData: DataTableState['data'] = [];

        sortedUsersForSuperAdmin.forEach(user => {
            const { projects } = user;

            const basicDetail = <UserDetails firstName={user.firstName} lastName={user.lastName} email={user.email} status={user.status} />;

            const statusColumn = (
                <StatusChip label={user.status === 'Inactive' ? 'Deactivated' : user.status} status={getUserStatusFromEnum(user.status)} />
            );

            const totalRoles = getUserRoleCount(projectIdsForCurrentCustomer, user.projects || []);
            const roleColumn = <div className={`${styles.totalRoles} ${styles['totalRoles' + user.status]}`}>{totalRoles}</div>;

            const assignedProjects = (
                <div className={styles.projectTags}>
                    {projects
                        ?.filter(project => projectIdsForCurrentCustomer.includes(project.projectId))
                        .map((project, index) => {
                            const projectName = getProjectNameForProjectId(currentCustomerProjects, project.projectId);
                            return (
                                <span key={index} className={`${styles.projectTag} ${styles['projectTag' + user.status]}`}>
                                    {projectName}
                                </span>
                            );
                        })}
                </div>
            );

            const isCustomerAdminUser = checkIfCustomerAdmin(user.customers, currentCustomerId);
            const customerAdminLogo = isCustomerAdminUser ? <CustomerAdminIcon className={styles.customerAdminLogo} /> : '';

            const userType = user.is_internal_user ? UserType.INTERNAL : UserType.CUSTOMER;
            const userTypeTag = <UserTypeTag userType={userType} />;

            const tableRowData = [user.cognitoId, customerAdminLogo, basicDetail, statusColumn, userTypeTag, roleColumn, assignedProjects];

            const actions = (
                <div className={styles.actionButtons}>
                    <TertiaryButton
                        className={styles.actions}
                        onClick={e => {
                            e.stopPropagation();
                            dispatch(updateGeneralUiState({ shouldShowEditUserModal: true }));
                            dispatch(setEditUserFormState({ ...user, projects: user.projects || [] }));
                            dispatch(setEditUserFormMode(Access.UPDATE));
                        }}
                    >
                        <CreateOutlinedIcon /> <span>EDIT</span>
                    </TertiaryButton>
                </div>
            );

            //In case of Onboarding Manger - Display Edit option only for the Customer Admin User/Row
            const shouldShowEditButton = isCurrentUserOnboardingManager ? isCustomerAdminUser : true;

            tableRowData.push(shouldShowEditButton ? actions : '');

            tableData.push(tableRowData);
        });

        setTableState(prev => ({
            ...prev,
            data: tableData,
            currentPageNumber: currentDataForSuperAdmin?.pagination.page || 1,
        }));
    }, [currentDataForSuperAdmin, currentCustomerProjects]);

    if (!tableState.data || isFetchingUsersForSuperAdmin) {
        return <Loader />;
    }

    const emptyStateMessage = (
        <div className={styles.noRecordsMessage}>
            There were no user accounts found matching this selection. Adjust the filters to find users in your organisation.
        </div>
    );

    const onPageChange = (page: number) => {
        dispatch(updaterUserManagementListFilterState({ currentPageNumber: page }));
    };

    const isTableLoading = tableState.currentPageNumber !== currentDataForSuperAdmin?.pagination.page;

    return (
        <div className={styles.userManagementList}>
            {isTableLoading && (
                <div className='absolute bg-white opacity-70 size-full z-[2]'>
                    <Loader />
                </div>
            )}
            <DataTable
                state={tableState.data ? tableState : undefined}
                columns={tableColumns}
                onStateChange={setTableState}
                emptyStateMessage={emptyStateMessage}
                onRowSelect={onRowSelect}
                onPageChange={onPageChange}
                totalRowsForPagination={currentDataForSuperAdmin?.pagination.total}
            />
        </div>
    );
};

export default SuperAdminUserManagementList;
